import { withStyles } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import ShoppingCart from "@material-ui/icons/ShoppingCart";
import DynamicString from "@passportinc/hydra-core/components/DynamicString";
import Loading from "@passportinc/hydra-core/components/Loading";
import { numberFormat } from "@passportinc/hydra-core/utils/common";
import { Accordion, Button, DataTable, LabelHorizontal, withDrawer } from "@passportinc/hydraulics";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import fp from "lodash/fp";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { queryClient } from "../../../App";
import * as constants from "../../../constants";
import useCartQuote, { primeCartQuote } from "../../../hooks/useCartQuote";
import { permitTypesQueryOptions } from "../../../hooks/usePermitTypes";
import { getApplicationById, getCartInformation } from "../../../services/permits";
import { isPermitTypeVisible } from "../../../utils";
import EmptyState from "../../EmptyState";
import withToast from "../../withToast";
import CartItem from "../CartItem";

const { CART_STORAGE_DAYS, CART_TYPES, CHARGE_TYPES, FREE_RATE_ID } = constants;
const { CHARGE_PER_PERMIT, CHARGE_PER_ORDER } = CHARGE_TYPES;

const CART_EXPIRATION_DATE = Date.now() - CART_STORAGE_DAYS * 24 * 60 * 60 * 1000;

export const styles = (theme) => ({
	checkoutButton: {
		width: "100%",
		height: 40,
		marginTop: 20,
		borderRadius: 4,
		backgroundColor: theme.palette.primary.main,
	},
	checkoutButtonSpinner: { marginRight: 8 },
	addMoreButton: {
		width: "100%",
		height: 40,
		marginTop: 20,
		borderRadius: 4,
		backgroundColor: theme.palette.primary.secondary,
	},
	labelRoot: {
		marginTop: 5,
		backgroundColor: "rgb(234, 237, 239)",
		height: 40,
		padding: "0px 10px",
		display: "flex",
		alignItems: "center",
		borderRadius: 2,
	},
	labelContent: {
		...theme.typography.body,
		fontSize: "0.875rem",
		fontWeight: 700,
		textTransform: "none",
	},
	labelHeader: {
		...theme.typography.body,
		fontSize: "0.875rem",
		fontWeight: 700,
		textTransform: "none",
	},
	taxesAndFees: {
		marginTop: 20,
		backgroundColor: "rgb(234, 237, 239)",
		paddingTop: 10,
		fontSize: "0.9rem",
		fontWeight: 500,
	},
	taxesAndFeesItem: {
		display: "flex",
		justifyContent: "space-between",
		padding: 10,
	},
	disclaimerText: {
		fontSize: "0.8rem",
	},
	cartItems: {
		paddingLeft: 24,
		paddingRight: 24,
	},
	href: {
		color: "#fffff",
		textDecoration: "none",
	},
	subHeader: {
		...theme.typography.h4,
		margin: "0px 0px 20px 0px",
		fontWeight: "normal",
	},
	uponAcceptance: {
		margin: "40px 0px 20px 0px",
	},
	section: {
		marginBottom: 40,
	},
	accordionTitle: {
		color: "grey !important",
	},
	accordionBorder: {
		border: "none !important",
	},
	tableWrapper: {
		borderLeft: "none !important",
		borderRight: "none !important",
	},
});

export const findMatchingItemAndFilterRest = (itemsInProps, itemsInState) =>
	fp.compose(
		fp.compact,
		fp.map((item) =>
			_.find(itemsInState, {
				id: item.id,
				type: item.type,
			}),
		),
	)(itemsInProps);

/**
 * Reconciles local cart-item state against the latest cart items from props.
 * @param {Array<Object>} stateItems The items currently held in component state
 * @param {Array<Object>} cartItems The items currently in the cart (props)
 * @returns {Array<Object>} The reconciled item list
 */
export const computeUpdatedItems = (stateItems, cartItems) => findMatchingItemAndFilterRest(cartItems, stateItems);

/**
 * Retrieves data for applications in cart and finds ones that have expired.
 * Also finds items that have been in cart longer than CART_EXPIRATION_DATE.
 * @param {Object} cart The cart prop, `{ items: Array<Object> }`
 * @returns {Array<Object>} List of all items that have expired
 */
export const fetchCartApplications = (cart) =>
	Promise.map(
		cart.items.filter((i) => i.type === CART_TYPES.application),
		(a) => getApplicationById(a.id),
	);

/**
 * Cart items whose permit type is no longer offered publicly.
 *
 * Pure so the mount sweep can share one application fetch with the expiry check.
 * Sweeps `permit_application` items only - the same set `fetchCartApplications` retrieves.
 * The other cart types, `change_request_application` included, are left alone here, matching
 * what the expiry sweep already does.
 * @param {Array<Object>} applications - Application responses from `fetchCartApplications`.
 * @param {Array<Object>} types - Permit types from `usePermitTypes`.
 * @returns {Array<Object>} Cart-item descriptors `{ id, type }` to remove.
 */
export const findHiddenTypeCartItems = (applications, types) =>
	applications
		// The id guard belongs here, not in the map: a response with no `data` would otherwise
		// survive the filter and throw downstream, and the mount catch empties the whole cart.
		.filter((a) => a?.data?.id && !isPermitTypeVisible(a.data.type_id, types))
		.map((a) => ({ id: a.data.id, type: CART_TYPES.application }));

export const findExpiredCartItems = async (cart, cartApplications) => {
	const applications = cartApplications || (await fetchCartApplications(cart));

	const expiredApplications = _.map(
		_.filter(applications, (a) => _.get(a, "data.end_date") && moment().isAfter(a.data.end_date)),
		(a) => ({ id: a.data.id, type: CART_TYPES.application }),
	);

	const oldItems = _.filter(cart.items, (i) => i.date_added < CART_EXPIRATION_DATE);

	return [...expiredApplications, ...oldItems];
};

/**
 * Removes expired items and informs the user.
 * @param {Array<Object>} items The items that have expired
 * @param {Object} deps Injected dependencies
 * @param {Function} deps.onRemoveCartItem Called with `{ id, type }` for each removed item
 * @param {Function} deps.onToast Called to surface the expiry toast
 * @param {Object} deps.VARIANTS Toast variant constants
 * @returns {void}
 */
export const removeExpiredCartItems = (items, { onRemoveCartItem, onToast, VARIANTS }) => {
	onToast({
		message: DynamicString.resolve(
			"hydra_permits_cart_drawer_expired_item_message",
			"One or more of your cart items have expired.",
		),
		variant: VARIANTS.info,
	});
	_.map(items, (i) => onRemoveCartItem({ id: i.id, type: i.type }));
};

/**
 * Removes items whose permit type is no longer offered publicly and informs the user.
 * @param {Array<Object>} items The items whose type is no longer available
 * @param {Object} deps Injected dependencies
 * @param {Function} deps.onRemoveCartItem Called with `{ id, type }` for each removed item
 * @param {Function} deps.onToast Called to surface the removal notice
 * @param {Object} deps.VARIANTS Toast variant constants
 * @returns {void}
 */
export const removeHiddenTypeCartItems = (items, { onRemoveCartItem, onToast, VARIANTS }) => {
	onToast({
		message: DynamicString.resolve(
			"hydra_permits_cart_drawer_unavailable_item_message",
			"One or more permits in your cart are no longer available online.",
		),
		variant: VARIANTS.info,
	});
	items.forEach((i) => {
		onRemoveCartItem({ id: i.id, type: i.type });
	});
};

const sumValue = (items, parseKey) => _.reduce(items, (sum, item) => sum + _.get(item, parseKey, 0), 0);

const currencyFormat = (value) => numberFormat(value, { style: "currency" });

const getAllTotals = (items, isPerOrderFee, orderLevelFee) => {
	const preTaxTotal = sumValue(items, "rate.pre_tax_total");
	const taxes = sumValue(items, "rate.taxes");
	let fees = isPerOrderFee ? orderLevelFee : sumValue(items, "rate.fees.transaction_fee");
	if (!items.length) fees = 0;

	return {
		preTaxTotal,
		taxes,
		fees,
		totalDue: preTaxTotal + taxes + fees,
	};
};

/**
 * @param {Object} config Render config
 * @param {Object} config.classes Style classes
 * @param {Function} config.onClose Called when a rendered CartItem's close affordance is used
 * @param {Function} config.onRemoveItem Called with an id when the cart item is removed by the user.
 * @returns {Function} `(items, isWaitlisted) => Array<Components>` — a list of components.
 */
export const renderCartItems =
	({ classes, onClose, onRemoveItem }) =>
	(items, isWaitlisted) =>
		_.map(items, (item) => {
			let text = _.get(item, "permission.name");
			if (item.type === CART_TYPES.change_request_application) {
				const headers = [
					{ value: "Field", field: "fieldName" },
					{ value: "Updated Information", field: "updatedInformation" },
				];
				const values = _.get(item, "changeList");
				text = (
					<Accordion
						title="View Details"
						classes={{
							heading: classes.accordionTitle,
							summary: classes.accordionBorder,
						}}
					>
						<div style={{ padding: 25 }}>
							<div style={{ paddingBottom: 15 }}>{`Permit #${_.get(item, "number")}`}</div>
							<DataTable
								classes={{ tableWrapper: classes.tableWrapper }}
								headers={headers}
								values={values}
								sortField="name"
							/>
						</div>
					</Accordion>
				);
			}

			let subtitle;
			const permitSubtitle = DynamicString.resolve("hydra_permits_card_drawer_waitlisted_subtitle", "Permit");
			if (_.includes(["permit_extension", "permit_waitlist_extension"], item.type)) {
				subtitle = isWaitlisted
					? permitSubtitle
					: DynamicString.resolve(
							"hydra_permits_card_drawer_rate_date_name_renewal_subtitle",
							item.type === "permit_waitlist_extension"
								? "%rate_date_name% Waitlist Renewal"
								: "%rate_date_name% Renewal",
						).replace("%rate_date_name%", _.get(item, "rate.dates.name"));
			} else if (item.accept_waitlist) {
				subtitle = isWaitlisted
					? permitSubtitle
					: DynamicString.resolve("hydra_permits_card_drawer_subtitle", "Waitlist");
			} else if (item.type === CART_TYPES.pass_added) {
				subtitle = `Quantity: ${item.quantity}`;
			}

			return (
				<CartItem
					key={item.id}
					item={item}
					title={
						item.type === CART_TYPES.change_request_application
							? "Permit Change Request Fee"
							: _.get(item, "permit_type.name")
					}
					subtitle={subtitle}
					value={isWaitlisted ? _.get(item, "rate.permit_rate.total", 0) : _.get(item, "rate.pre_tax_total", 0)}
					onRemoveItem={_.partial(onRemoveItem, {
						id: item.id,
						type: item.type,
					})}
					text={text}
					onClose={onClose}
					isWaitlisted={isWaitlisted}
				/>
			);
		});

/**
 * CartDetailsPage React Component
 * @param {Object} props - forwarded from CartDrawerWithQuote / withStyles / withRouter / withToast / withDrawer
 * @returns {React.ReactElement} CartDetailsPage component
 */
export const CartDrawer = (props) => {
	const [state, setState] = useState({
		isErrored: false,
		items: [],
		isLoadingCartItems: false,
		hasReconciled: false,
	});

	const prevPropsRef = useRef(props);
	const didMountRef = useRef(false);

	// Mirrors componentDidMount: hits the cart endpoint with the items in the cart,
	// to retrieve cart info, exactly once per mount.
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only by design (mirrors componentDidMount, which reads this.props once at method entry); must run exactly once regardless of later prop changes.
	useEffect(() => {
		const run = async () => {
			const { cart, onRemoveCartItem, onCacheQuote, onToast, VARIANTS } = props;
			if (!cart.items.length) {
				setState((prev) => ({ ...prev, hasReconciled: true }));
				return;
			}

			setState((prev) => ({ ...prev, isLoadingCartItems: true }));

			try {
				const cartApplications = await fetchCartApplications(cart);
				const expiredItems = await findExpiredCartItems(cart, cartApplications);
				// An effect needs this once, not on every render, so it awaits the query rather
				// than subscribing to it - subscribing would either race the sweep or block the
				// drawer's instant paint. Resolves from cache once any page has warmed it.
				const permitTypes = await queryClient.fetchQuery({ ...permitTypesQueryOptions, retry: 0 }).catch(() => []);
				// An expired item on a hidden type belongs to one removal, not two: both sweeps
				// toast per batch, so leaving it in both stacks two notices for a single item.
				const expiredIds = new Set(expiredItems.map((i) => i.id));
				const hiddenItems = findHiddenTypeCartItems(cartApplications, permitTypes).filter((i) => !expiredIds.has(i.id));

				if (expiredItems.length) removeExpiredCartItems(expiredItems, { onRemoveCartItem, onToast, VARIANTS });
				if (hiddenItems.length) removeHiddenTypeCartItems(hiddenItems, { onRemoveCartItem, onToast, VARIANTS });

				const removedIds = [...expiredItems, ...hiddenItems].map((i) => i.id);
				const cartItems = removedIds.length ? cart.items.filter((i) => !removedIds.includes(i.id)) : cart.items;

				if (!cartItems.length) return;

				const res = await getCartInformation(cartItems);

				// Warm the shared display cache under the exact request the reconcile
				// used, so the next drawer open for this cart paints instantly.
				onCacheQuote(cartItems, _.get(res, "data", {}));

				setState((prev) => ({
					...prev,
					items: _.get(res, "data.items", []),
					productTotals: _.get(res, "data.product_totals", {}),
				}));
			} catch (_e) {
				_.map(cart.items, (item) => onRemoveCartItem({ id: item.id, type: item.type }));
				setState((prev) => ({ ...prev, isErrored: true }));
			} finally {
				// hasReconciled gates checkout reachability: the expiry sweep and
				// reconcile fetch have now settled, so a cached instant-paint can hand
				// off to the sanitized, freshly priced cart.
				setState((prev) => ({ ...prev, isLoadingCartItems: false, hasReconciled: true }));
			}
		};
		run();
	}, []);

	// Mirrors componentDidUpdate + maybeUpdateCartItems: decides whether the cart items
	// in props (redux) changed, and if so, checks local state to find what update happened.
	// Runs after every commit (no dependency array, like componentDidUpdate), but only acts
	// when the parent handed down a new props object (prevProps !== this.props) — self-setState
	// re-renders keep the same props reference and must not re-trigger the reconcile.
	// useLayoutEffect (not useEffect) on purpose: componentDidUpdate ran synchronously
	// before the browser painted, so an item removed from the cart never had a chance
	// to flash on screen. A passive effect runs after paint instead, so on removal the
	// drawer would briefly paint the stale (pre-removal) frame before pruning state.
	// useLayoutEffect restores the class's pre-paint timing.
	useLayoutEffect(() => {
		if (!didMountRef.current) {
			didMountRef.current = true;
			prevPropsRef.current = props;
			return;
		}

		if (prevPropsRef.current !== props) {
			const { cart } = props;

			if (
				_.get(cart, "items.length") !== _.get(state.items, "length") &&
				!state.isLoadingCartItems &&
				!state.isErrored
			) {
				setState((prev) => ({ ...prev, items: computeUpdatedItems(state.items, cart.items) }));
			}
		}

		prevPropsRef.current = props;
	});

	/**
	 * Triggered when the user clicks the "Check Out" button.
	 * @returns {undefined}
	 */
	const handleCheckoutClick = () => {
		props.onClose();
		props.history.push("/permits/checkout");
	};

	/**
	 * Called when the user clicks the "Add Additional Items to Cart" button.
	 * @returns {undefined}
	 */
	const handleAddMoreClick = () => {
		props.onClose();
		props.history.push("/permits/dashboard");
	};

	const { isLoadingCartItems, items, productTotals, isErrored, hasReconciled } = state;
	const { classes, onClose, onRemoveCartItem, settings, cachedQuote } = props;
	const hasStateItems = Boolean(_.get(items, "length"));
	// Paint the last cached quote instantly on a warm open, before this drawer's
	// own expiry-sweep + reconcile fetch settles. Once reconcile completes, the
	// freshly fetched state.items is authoritative even when it comes back empty.
	const useCache = !hasStateItems && !hasReconciled && Boolean(_.get(cachedQuote, "items.length"));
	const displayItems = useCache ? cachedQuote.items : items;
	const displayProductTotals = useCache ? _.get(cachedQuote, "product_totals", {}) : productTotals;
	const hasItemsInCart = Boolean(_.get(displayItems, "length"));
	// Expiration ordering: even when cached content paints immediately, checkout
	// stays gated until the expiry sweep + reconcile fetch have settled on the
	// real (sanitized) state cart.
	const checkoutReady = hasStateItems && hasReconciled;
	// Busy affordance only: while displayed items await the expiry sweep +
	// reconcile settle, the CTA looks in-flight instead of dead. Gate unchanged.
	const checkoutBusy = hasItemsInCart && !checkoutReady && !isErrored;
	// Full spinner ONLY when there is nothing cached to show; otherwise the
	// cached content stays visible while the Check Out CTA carries the busy
	// affordance (checkoutBusy, below).
	const showFullSpinner = isLoadingCartItems && !useCache;
	const isPerOrderFee = CHARGE_PER_ORDER === _.get(settings, "permit_fee_scope", CHARGE_PER_PERMIT);
	const orderLevelFee = _.get(displayProductTotals, "permits.orderServiceFee", 0);
	const { preTaxTotal, taxes, fees, totalDue } = getAllTotals(displayItems, isPerOrderFee, orderLevelFee);

	const regularPermits = _.filter(
		displayItems,
		(item) => !(_.get(item, "permit_type.settings.is_free") && _.get(item, "rate.rate_id") === FREE_RATE_ID),
	);
	const itemsChargedImmediately = _.filter(
		regularPermits,
		(item) =>
			_.get(item, "charge_now") &&
			_.get(item, "type") !== "change_request_application" &&
			!_.get(item, "approval_required"),
	);
	const itemsChargedUponAcceptance = _.filter(
		regularPermits,
		(item) =>
			_.get(item, "charge_now") &&
			_.get(item, "rate.permit_rate") &&
			_.get(item, "type") !== "change_request_application",
	);
	const itemsChargedOnApproval = _.filter(
		regularPermits,
		(item) =>
			!_.get(item, "charge_now") ||
			_.get(item, "type") === "change_request_application" ||
			_.get(item, "approval_required"),
	);

	const freePermits = _.filter(
		displayItems,
		(item) => _.get(item, "permit_type.settings.is_free") && _.get(item, "rate.rate_id") === FREE_RATE_ID,
	);
	const itemsActiveImmediately = _.filter(
		freePermits,
		(item) => _.get(item, "charge_now") && !_.get(item, "rate.permit_rate") && !_.get(item, "approval_required"),
	);
	const itemsActiveUponAcceptance = _.filter(
		freePermits,
		(item) => _.get(item, "charge_now") && _.get(item, "rate.permit_rate"),
	);
	const itemsActiveOnApproval = _.filter(
		freePermits,
		(item) => !_.get(item, "charge_now") || _.get(item, "approval_required"),
	);

	const renderItemsWithHandler = renderCartItems({ classes, onClose, onRemoveItem: onRemoveCartItem });

	return (
		<div className={classes.cartItems}>
			{showFullSpinner ? (
				<Loading />
			) : (
				<React.Fragment>
					<div id="cartitems">
						{hasItemsInCart ? (
							<React.Fragment>
								{Boolean(itemsChargedImmediately.length) && (
									<div className={classes.section}>
										<h4 className={classes.subHeader}>
											<DynamicString
												lookupPath="hydra_permits_cart_drawer_items_charged_immediately"
												defaultText="Items Charged Immediately"
											/>
										</h4>
										{renderItemsWithHandler(itemsChargedImmediately)}
									</div>
								)}
								{Boolean(itemsActiveImmediately.length) && (
									<div className={classes.section}>
										<h4 className={classes.subHeader}>
											<DynamicString
												lookupPath="hydra_permits_cart_drawer_items_charged_immediately"
												defaultText="Items Active Immediately"
											/>
										</h4>
										{renderItemsWithHandler(itemsActiveImmediately)}
									</div>
								)}
								{Boolean(itemsChargedOnApproval.length) && (
									<div className={classes.section}>
										<h4 className={classes.subHeader}>
											<DynamicString
												lookupPath="hydra_permits_cart_drawer_items_charged_on_approval"
												defaultText="Items Charged Upon Approval"
											/>
										</h4>
										{renderItemsWithHandler(itemsChargedOnApproval)}
									</div>
								)}
								{Boolean(itemsActiveOnApproval.length) && (
									<div className={classes.section}>
										<h4 className={classes.subHeader}>
											<DynamicString
												lookupPath="hydra_permits_cart_drawer_items_charged_on_approval"
												defaultText="Items Active Upon Approval"
											/>
										</h4>
										{renderItemsWithHandler(itemsActiveOnApproval)}
									</div>
								)}
							</React.Fragment>
						) : (
							<EmptyState Icon={isErrored ? ErrorOutline : ShoppingCart}>
								{!isErrored ? (
									<DynamicString lookupPath="hydra_permits_cart_drawer_empty_state" defaultText="Your cart is empty" />
								) : (
									<DynamicString
										lookupPath="hydra_permits_cart_drawer_error_state"
										defaultText="An error occurred while retrieving your cart information"
									/>
								)}
							</EmptyState>
						)}
					</div>
					<div className={classes.taxesAndFees}>
						<div data-item="permitCost" className={classes.taxesAndFeesItem}>
							<span className={classes.pricesLabel}>Permit Cost</span>
							<span className={classes.pricesValue}>{currencyFormat(preTaxTotal)}</span>
						</div>
						{taxes > 0 && (
							<div data-item="taxes" className={classes.taxesAndFeesItem}>
								<span className={classes.pricesLabel}>Taxes</span>
								<span className={classes.pricesValue}>{currencyFormat(taxes)}</span>
							</div>
						)}
						{fees > 0 && (
							<div data-item="serviceFees" className={classes.taxesAndFeesItem}>
								<span className={classes.pricesLabel}>Service Fees</span>
								<span className={classes.pricesValue}>{currencyFormat(fees)}</span>
							</div>
						)}
						<LabelHorizontal
							label={currencyFormat(totalDue)}
							header={DynamicString.resolve("hydra_permits_card_drawer_subtotal_cost_header", "Total Due")}
							classes={{
								root: classes.labelRoot,
								label: classes.labelContent,
								header: classes.labelHeader,
							}}
						/>
					</div>
					<div className="disclaimer">
						{fees > 0 && (
							<p className={classes.disclaimerText}>
								<DynamicString
									lookupPath="hydra_permits_fee_disclaimer"
									defaultText="* Service fees are not refundable"
								/>
							</p>
						)}
					</div>
					<Button
						color="primary"
						onClick={!checkoutReady ? _.noop : handleCheckoutClick}
						variant="contained"
						disabled={!checkoutReady}
						className={classes.checkoutButton}
					>
						{checkoutBusy && (
							<CircularProgress
								size={16}
								color="inherit"
								aria-hidden="true"
								className={classes.checkoutButtonSpinner}
							/>
						)}
						{checkoutBusy ? (
							<DynamicString
								lookupPath="hydra_permits_cart_drawer_validating_cart_text"
								defaultText="Validating Cart..."
							/>
						) : (
							<DynamicString lookupPath="hydra_permits_cart_drawer_checkout_button_text" defaultText="Check Out" />
						)}
					</Button>
					<Button color="primary" onClick={handleAddMoreClick} variant="outlined" className={classes.addMoreButton}>
						<DynamicString
							lookupPath="hydra_permits_cart_drawer_add_more_button_text"
							defaultText="Add Additional Items to Cart"
						/>
					</Button>
					{Boolean(itemsChargedUponAcceptance.length) && (
						<div className={classes.uponAcceptance}>
							<h4 className={classes.subHeader}>
								<DynamicString
									lookupPath="hydra_permits_cart_drawer_items_charged_upon_offer_acceptance"
									defaultText="Items Charged Upon Waitlist Offer Acceptance"
								/>
							</h4>
							{renderItemsWithHandler(itemsChargedUponAcceptance, true)}
						</div>
					)}
					{Boolean(itemsActiveUponAcceptance.length) && (
						<div className={classes.uponAcceptance}>
							<h4 className={classes.subHeader}>
								<DynamicString
									lookupPath="hydra_permits_cart_drawer_items_charged_upon_offer_acceptance"
									defaultText="Items Active Upon Waitlist Offer Acceptance"
								/>
							</h4>
							{renderItemsWithHandler(itemsActiveUponAcceptance, true)}
						</div>
					)}
				</React.Fragment>
			)}
		</div>
	);
};

CartDrawer.propTypes = {
	classes: PropTypes.shape({}).isRequired,
	history: PropTypes.shape({
		push: PropTypes.func.isRequired,
	}).isRequired,
	cart: PropTypes.shape({
		items: PropTypes.array.isRequired,
	}).isRequired,
	settings: PropTypes.shape({}).isRequired,
	onClose: PropTypes.func.isRequired,
	onRemoveCartItem: PropTypes.func.isRequired,
	onToast: PropTypes.func.isRequired,
	VARIANTS: PropTypes.shape({}).isRequired,
	cachedQuote: PropTypes.shape({}),
	onCacheQuote: PropTypes.func,
};

CartDrawer.defaultProps = {
	cachedQuote: null,
	onCacheQuote: _.noop,
};

/**
 * Functional wrapper that supplies the class component with a display quote read
 * from the shared react-query cache (`useCartQuote`). A warm cart (primed by a
 * prior open, or by checkout) paints instantly instead of blocking on a fetch.
 *
 * The class keeps its own expiry-sweep + reconcile fetch — that fetch remains the
 * authority that gates checkout. This wrapper only hands down the cached snapshot
 * for the instant paint plus a primer callback that warms the cache after each
 * settled reconcile. `enabled: false` means the hook never issues its own request,
 * so the drawer's per-open request count is unchanged (the class issues the one
 * fetch; the hook merely subscribes to the cache entry).
 * @param {Object} props - forwarded untouched to the class component
 * @returns {React.ReactElement}
 */
export const CartDrawerWithQuote = (props) => {
	const queryClient = useQueryClient();
	const items = _.get(props, "cart.items", []);
	const { data: cachedQuote } = useCartQuote(items, undefined, { enabled: false });
	const onCacheQuote = React.useCallback(
		(requestedItems, data) => primeCartQuote(queryClient, requestedItems, undefined, data),
		[queryClient],
	);

	return <CartDrawer {...props} cachedQuote={cachedQuote || null} onCacheQuote={onCacheQuote} />;
};

export default fp.compose(withRouter, withToast, withDrawer, withStyles(styles))(CartDrawerWithQuote);
